import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ExternalLink, CheckCircle, Clock, Package, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};
const fmt = (p) => `R${Number(p).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;

const STATUS_CONFIG = {
  requested: { label: 'Awaiting Shopper', icon: <Clock size={14} />, color: 'text-[#D4AF37] bg-[#D4AF37]/10' },
  confirmed: { label: 'Confirmed', icon: <CheckCircle size={14} />, color: 'text-[#2E7D32] bg-[#2E7D32]/10' },
  payment_sent: { label: 'Payment Ready', icon: <CreditCard size={14} />, color: 'text-[#0A0A0A] bg-[#D4AF37]/20' },
  paid: { label: 'Paid', icon: <CheckCircle size={14} />, color: 'text-[#2E7D32] bg-[#2E7D32]/10' },
  fulfilled: { label: 'Fulfilled', icon: <Package size={14} />, color: 'text-[#2E7D32] bg-[#2E7D32]/10' },
};

export default function Orders() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollMsg, setPollMsg] = useState('');
  const pollingRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    fetchOrders();

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPollMsg('Verifying payment...');
      pollPaymentStatus(sessionId, 0);
    }

    return () => { if (pollingRef.current) clearTimeout(pollingRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch { /* silently handle order fetch error */
      devErr('[Orders] fetchOrders failed');
    }
    finally { setLoading(false); }
  };

  const pollPaymentStatus = async (sessionId, attempt) => {
    if (attempt >= 6) { setPollMsg(''); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setPollMsg('Payment confirmed!');
        fetchOrders();
        setTimeout(() => setPollMsg(''), 4000);
      } else {
        pollingRef.current = setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (e) {
      pollingRef.current = setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-16 lg:pt-20 pb-20 lg:pb-0" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">Tracking</p>
        <h1 className="font-playfair text-4xl text-[#0A0A0A] mb-12">My Orders</h1>

        {pollMsg && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3 mb-8 text-sm font-work text-[#0A0A0A]" data-testid="poll-message">
            {pollMsg}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="text-center py-24">
            <Package size={48} className="mx-auto text-[#E5E5E5] mb-6" />
            <p className="font-playfair text-2xl text-[#0A0A0A] mb-4">No orders yet</p>
            <p className="text-[#999999] text-sm font-work mb-8">Request purchases from your bag to see them here</p>
            <Link to="/bag">
              <button className="bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-10 py-4 text-xs uppercase tracking-widest font-work">
                View Bag
              </button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {orders.map(order => {
              const p = order.product;
              const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.requested;
              return (
                <div key={order.bag_item_id} className="border border-[#E5E5E5] p-6" data-testid={`order-${order.bag_item_id}`}>
                  <div className="flex gap-5">
                    <div
                      className="w-20 h-24 flex-shrink-0 bg-[#F9F9F9] overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/product/${p.product_id}`)}
                    >
                      <img src={p.images?.[0]} alt={p.name} className="object-cover w-full h-full" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work">{p.designer}</p>
                          <p className="text-sm font-medium text-[#0A0A0A] font-work">{p.name}</p>
                          {order.size && <p className="text-xs text-[#999999] font-work mt-0.5">Size: {order.size}</p>}
                        </div>
                        <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 font-work ${st.color}`}>
                          {st.icon} {st.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-3">
                        <p className="text-[#0A0A0A] font-semibold font-work">{fmt(p.final_price)}</p>
                        <p className="text-xs text-[#D4AF37] font-work">Save {fmt(p.savings)}</p>
                      </div>

                      {/* Payment Link */}
                      {order.status === 'payment_sent' && order.payment_url && (
                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3">
                          <CreditCard size={16} className="text-[#D4AF37] flex-shrink-0 hidden sm:block" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-[#0A0A0A] font-work mb-0.5">Your payment link is ready</p>
                            <p className="text-[10px] text-[#555555] font-work">Complete your payment to confirm the order</p>
                          </div>
                          <a
                            href={order.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-4 py-3 text-xs uppercase tracking-wider font-work w-full sm:w-auto"
                            data-testid={`pay-btn-${order.bag_item_id}`}
                          >
                            Pay Now <ExternalLink size={12} />
                          </a>
                        </div>
                      )}

                      {order.admin_notes && (
                        <p className="mt-3 text-xs text-[#555555] font-work italic">Note: {order.admin_notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
